import { Component, Project, TextFile } from "projen";

/** Field values for the canonical cross-repo `## Requirement` block. */
export interface RequirementBlockFields {
  /** Requirement ID (e.g. `FR-042`). */
  readonly id: string;

  /** URL to the canonical requirement doc in the planning repo. */
  readonly specUrl: string;

  /** Requirement tier (Platform | Industry | Customer Workflow | Consumer Application). */
  readonly tier: string;

  /** Customer scope, or `—`. */
  readonly customer: string;
}

/**
 * Render the canonical cross-repo `## Requirement` traceability block.
 * Shared by the requirement issue template (#824) and the fan-out
 * workflow (#825) so a human-filed issue and a bot-fanned-out issue
 * produce byte-identical blocks.
 */
export function renderRequirementBlock(fields: RequirementBlockFields): string {
  return [
    "## Requirement",
    "",
    `- **ID:** ${fields.id}`,
    `- **Spec:** ${fields.specUrl}`,
    `- **Tier:** ${fields.tier}`,
    `- **Customer:** ${fields.customer}`,
  ].join("\n");
}

/** Options for the requirement issue template component. */
export interface RequirementIssueTemplateOptions {
  /**
   * Output path for the emitted GitHub issue template.
   * @default ".github/ISSUE_TEMPLATE/requirement-impl.md"
   */
  readonly path?: string;

  /**
   * Static labels applied by the template front-matter.
   * @default ["type:feat"]
   */
  readonly labels?: ReadonlyArray<string>;
}

const DEFAULT_TEMPLATE_PATH = ".github/ISSUE_TEMPLATE/requirement-impl.md";
const DEFAULT_TEMPLATE_LABELS: ReadonlyArray<string> = ["type:feat"];

/**
 * Emits a GitHub markdown issue template that carries the canonical
 * cross-repo `## Requirement` traceability block. A human opening an
 * issue from this template records the requirement ID, its spec URL,
 * tier, and customer scope, and is prompted to add a `req:<ID>` label
 * so the issue is traceable to the requirement across repositories.
 *
 * The `## Requirement` block is rendered by {@link renderRequirementBlock}
 * so a human-filed issue and a bot-fanned-out issue (the fan-out
 * workflow in #825) produce byte-identical blocks.
 *
 * @see docs/src/content/docs/packages/\@codedrifters/configulator/requirement-traceability.md
 */
export class RequirementIssueTemplate extends Component {
  constructor(project: Project, options: RequirementIssueTemplateOptions = {}) {
    super(project);

    const path = options.path ?? DEFAULT_TEMPLATE_PATH;
    const labels = options.labels ?? DEFAULT_TEMPLATE_LABELS;
    const labelsYaml = `[${labels.map((l) => `"${l}"`).join(", ")}]`;

    // The four `## Requirement` lines come from renderRequirementBlock so
    // the block structure is shared with the fan-out workflow (#825). The
    // placeholder values are HTML comments so the rendered issue shows an
    // inline hint next to each field.
    const requirementBlock = renderRequirementBlock({
      id: "<!-- e.g. FR-042 -->",
      specUrl:
        "<!-- URL to the canonical requirement doc in the planning repo -->",
      tier: "<!-- Platform | Industry | Customer Workflow | Consumer Application -->",
      customer: "<!-- customer scope, or — -->",
    });

    const lines = [
      "---",
      "name: Requirement implementation",
      "about: Implement a canonical requirement tracked in the planning repo",
      'title: "feat: implement <REQ-ID> — <short summary>"',
      `labels: ${labelsYaml}`,
      "---",
      "",
      ...requirementBlock.split("\n"),
      "",
      "> Add the `req:<ID>` label (e.g. `req:FR-042`) so this issue is traceable to the requirement across repos.",
      "",
      "## Summary",
      "",
      "<!-- What must change in THIS repo to satisfy the requirement above. -->",
      "",
      "## Acceptance Criteria",
      "",
      "- [ ]",
    ];

    // marker:false — projen's marker comment would corrupt the YAML
    // front-matter that GitHub parses at the top of the file.
    new TextFile(project, path, {
      marker: false,
      committed: true,
      lines,
    });
  }
}
